// app/javascript/landing/components/FullscreenVideoModal.tsx
import React, { useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

type FullscreenVideoModalProps = {
  open: boolean;
  src: string;
  title?: string;
  onClose: () => void;
};

export function FullscreenVideoModal({
  open,
  src,
  title,
  onClose,
}: FullscreenVideoModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Intentar autoplay con audio → fallback a mute si falla
  useEffect(() => {
    if (!open || !videoRef.current) return;

    const video = videoRef.current;
    video.muted = false; // intentar SIN mute
    video.playsInline = true;

    const tryPlay = async () => {
      try {
        await video.play(); // intenta reproducir con audio
      } catch (err) {
        // Falló → activar mute y reintentar
        video.muted = true;
        try {
          await video.play();
        } catch (subErr) {
          console.warn("Autoplay no permitido incluso con mute.", subErr);
        }
      }
    };

    tryPlay();
  }, [open, src]);

  // Cerrar con Esc
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden"; // bloquear scroll detrás

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, handleKeyDown]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 touch-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {/* Contenedor — evita cerrar al click interno */}
          <motion.div
            className="relative w-full max-w-5xl mx-auto px-4"
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón cerrar */}
            <button
              type="button"
              onClick={onClose}
              className="absolute -top-10 right-4 text-white/80 hover:text-white text-sm md:text-base"
            >
              Cerrar ✕
            </button>

            {title && (
              <h2 className="mb-3 text-center text-white text-lg md:text-xl font-semibold">
                {title}
              </h2>
            )}

            <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              <video
                ref={videoRef}
                src={src}
                className="w-full h-full object-contain"
                controls
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
